/** Review the report: a rehearsal to read, and the act of saying you have (B10).
 *
 * Two things the filing journey names and nothing offered.
 *
 * **A rehearsal** is the filing as it would read if it were issued now, drawn from today's figures
 * in a new tab. It is marked as a rehearsal on the page itself and carries no fingerprint, because
 * nothing is stored, hashed or recorded to make it. It is a link, not a request this screen makes:
 * the page is HTML the browser draws.
 *
 * **"I have read this report"** records, for a named person, that they read the report as it stood.
 * It is **not an approval** (the approval route above is, with ordered stages and a separate
 * reviewer), it does not say the report may be filed, and issuing does not wait for it. The panel
 * prints the server's own sentence saying so (`what_a_review_is`) rather than a second one written
 * here. Each recorded review says whether the report still reads the same, which the server derives
 * by rebuilding it - a review of an earlier version is shown as one, not removed.
 *
 * Who sees the control (design rule 6): only the roles that may record one. Anyone else reads the
 * list and is told who can.
 */

import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import type { Scope2Method } from "../api/queries";
import { rehearsalUrl, useMarkReviewed, useReportReviews } from "../api/reportReviews";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { StatusChip } from "../components/ui/StatusChip";
import { instantDate } from "../lib/format";
import own from "./Reports.module.css";
import styles from "./Screen.module.css";

/** Mirrored from `api/reports.py`'s `MAY_REVIEW`. A list, so a new role defaults to being told who
 * can rather than to a button that refuses. */
const MAY_REVIEW: readonly string[] = ["admin", "approver"];

export function ReportReviewPanel({
  obligationId,
  method,
}: {
  obligationId: string;
  method: Scope2Method;
}) {
  const { data: me } = useMe();
  const reader = useReader();
  const reviews = useReportReviews(obligationId);
  const mark = useMarkReviewed(obligationId, method);
  const mayReview = me ? MAY_REVIEW.includes(me.role) : false;
  const told = mark.data;

  return (
    <Panel
      title="Review the report"
      sub="Read it as it would be issued, then say you have"
      column="1 / 13"
    >
      <p className={own.fine}>
        <a
          href={rehearsalUrl(obligationId, method)}
          target="_blank"
          rel="noreferrer"
          data-testid="rehearsal-open"
        >
          Open a rehearsal of this filing
        </a>{" "}
        &mdash; how it would read if issued now, marked as a rehearsal. It is not the record and has
        no fingerprint; nothing is stored to make it.
      </p>

      {mayReview ? (
        <p className={own.fine}>
          <button
            type="button"
            // Secondary: the one primary on this tab is issuing a filing.
            className={styles.secondary}
            disabled={mark.isPending}
            onClick={() => mark.mutate()}
            data-testid="mark-reviewed"
          >
            {mark.isPending ? "Recording…" : "I have read this report"}
          </button>
        </p>
      ) : (
        <p className={own.fine} data-testid="mark-reviewed-who-can">
          An <strong>Approver</strong> or an <strong>Admin</strong> records that a report has been
          read.
        </p>
      )}
      <WriteFailed error={mark.error} of="that review" onReload={() => mark.reset()} />
      {told ? (
        <p className={own.fine} role="status" data-testid="mark-reviewed-done">
          {told.already_recorded
            ? "You had already recorded a review of this report as it reads now. Nothing was added."
            : "Recorded. You have read this report as it reads now."}
        </p>
      ) : null}

      {reviews.error ? (
        <ReadFailed
          of="The reviews of this report"
          error={reviews.error}
          onRetry={() => void reviews.refetch()}
        />
      ) : reviews.isPending ? (
        <p className={own.fine} role="status" aria-busy="true">
          Reading who has reviewed it…
        </p>
      ) : reviews.data.reviews.length === 0 ? (
        <Empty>Nobody has recorded that they have read this report yet.</Empty>
      ) : (
        <ul className={own.fine} data-testid="report-reviews">
          {reviews.data.reviews.map((review) => (
            <li key={review.id} data-testid="report-review" data-same={review.still_reads_the_same}>
              {review.reviewed_by === me?.user_id ? "You" : `Person ${review.reviewed_by.slice(0, 8)}`}{" "}
              read it on {instantDate(review.reviewed_at, reader)} ({review.scope2_method}-based, checks
              said {review.checks_verdict.replace(/_/g, " ")}){" "}
              {review.still_reads_the_same ? (
                <StatusChip tone="ok" size="sm">
                  Reads the same today
                </StatusChip>
              ) : (
                <StatusChip tone="warn" size="sm">
                  The report has changed since
                </StatusChip>
              )}
            </li>
          ))}
        </ul>
      )}
      {reviews.data ? (
        <p className={own.fine} data-testid="what-a-review-is">
          {reviews.data.what_a_review_is}
        </p>
      ) : null}
    </Panel>
  );
}
